// Swarm Surge — custom timed event (custom-events-v1 §E5). A migration
// front: for 15 s, spawn pressure ×2.5 and stream formations bias to enter
// from one seeded edge.
//
// Mechanics (spec): the event itself is just two world-scoped flags —
//   world.eventThreatMult = 2.5   read by updateDirector's threat-accrual
//                                 paths with `?? 1` (assault budget release
//                                 and the bonus-fight accrual; the wave's own
//                                 budget drains at the normal rate, the
//                                 surplus is the event's).
//   world.eventStreamEdge = 0..3  read by generateStreamFormation in place of
//                                 its seeded edge roll while set.
// Both flags are cleared in end() and by world.reset() (a restart mid-event
// never leaks pressure into the next run). The director changes are small
// additive hooks; the formation generators are not refactored.
//
// Determinism (GDD Appendix A §A.1): the edge comes from a private
// mulberry32 stream reseeded from world.runSeed in start(); everything else
// is flag-driven. No Math.random, no wall-clock.
//
// Perf: update() is a no-op — all the work happens inside the director.
//
// Visuals (wired by the parent, not here): 'phase_change'
//   { system: 'events', event_id: 'swarm_surge', phase: 'start'|'end', danger: 2, edge, duration }
// Suggested look: a creeping wall of spawn portals along the seeded edge.

import type { WorldState } from '../state.js';
import type { GameEventDef } from '../events-manager.js';
import { RngStream } from '../rng.js';

/** Event lifetime: 15 s at 60 Hz. */
const EVENT_DURATION_TICKS = 15 * 60;
/** Spawn-pressure multiplier while the front passes. */
const THREAT_MULT = 2.5;
/** Private stream salt: keeps the edge roll independent of every other system. */
const SURGE_STREAM_SALT = 0x5EEDED1E;

/**
 * Create the Swarm Surge event def. All mutable state lives in this closure
 * and is fully reset in start(), so the def instance is reusable across runs
 * and across multiple triggers within one run.
 */
export function createSwarmSurgeEvent(): GameEventDef {
  let stream = new RngStream(0);
  let edge = 0;

  return {
    id: 'swarm_surge',
    name: 'Swarm Surge',
    duration: EVENT_DURATION_TICKS,

    start(world: WorldState, tick: number): void {
      stream = new RngStream((world.runSeed ^ SURGE_STREAM_SALT) >>> 0);
      edge = stream.next_int(4); // 0: top, 1: right, 2: bottom, 3: left
      world.eventThreatMult = THREAT_MULT;
      world.eventStreamEdge = edge;
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'swarm_surge', phase: 'start', danger: 2, edge, duration: EVENT_DURATION_TICKS },
        tick
      );
    },

    update(_world: WorldState, _tick: number, _progress: number): void {
      // No per-tick work: the director reads the flags set in start().
    },

    end(world: WorldState, tick: number): void {
      world.eventThreatMult = undefined;
      world.eventStreamEdge = undefined;
      world.events.emit(
        'phase_change',
        { system: 'events', event_id: 'swarm_surge', phase: 'end', danger: 2, edge },
        tick
      );
    }
  };
}
